import { retryWithBackoff } from '../utils/retryUtils';

const AWS_REGION = 'us-west-2';
// Least-privilege role assumed (via Midway OIDC -> STS AssumeRoleWithWebIdentity)
// to SigV4-sign API Gateway calls. Account 425234301275 is the Storage Inventory
// burner; the role + Midway OIDC provider are created by
// LiveInfraStorageInventoryBurnerCDK's Gamma-StorageInventory stack.
const APP_ROLE_ARN = 'arn:aws:iam::425234301275:role/LiveInfraStorageInventoryWebsiteAccess';

let API_BASE_URL = '';
const settingsPromise = import.meta.env.DEV
  ? Promise.resolve()
  : fetch('/settings.json')
    .then(r => {
      if (!r.ok) throw new Error(`Failed to load settings.json: HTTP ${r.status}`);
      return r.json();
    })
    .then(s => {
      if (!s.apiBaseUrl) throw new Error('settings.json is missing apiBaseUrl');
      API_BASE_URL = s.apiBaseUrl;
    });

const encoder = new TextEncoder();

function generateNonce(): string {
  return btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))).replace(/[+/=]/g, '');
}

async function hmac(key: ArrayBuffer | Uint8Array, data: string): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return new Uint8Array(await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(data)));
}

async function sha256(data: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', encoder.encode(data));
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function getMidwayToken(): Promise<string> {
  const siteUrl = window.location.origin;
  const params = new URLSearchParams({ client_id: siteUrl, nonce: generateNonce(), redirect_uri: siteUrl, response_type: 'id_token', scope: 'openid' });
  const response = await fetch(`https://midway-auth.amazon.com/SSO?${params}`, { credentials: 'include' });
  const token = await response.text();
  if (!token || token.includes('<') || token.split('.').length !== 3) throw new Error('Failed to get Midway OIDC token');
  return token;
}

type Credentials = { accessKeyId: string; secretAccessKey: string; sessionToken: string; expiration: number };
const credentialCache = new Map<string, Credentials>();
const credentialPromises = new Map<string, Promise<Credentials>>();

async function fetchCredentialsForRole(roleArn: string): Promise<Credentials> {
  const token = await getMidwayToken();
  const body = new URLSearchParams({
    Action: 'AssumeRoleWithWebIdentity', Version: '2011-06-15', RoleArn: roleArn,
    RoleSessionName: 'StorageInventoryWebSession', WebIdentityToken: token, DurationSeconds: '3600',
  });
  const response = await fetch(`https://sts.${AWS_REGION}.amazonaws.com/`, {
    method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString(),
  });
  if (!response.ok) {
    const errorXml = await response.text();
    throw new Error(`STS AssumeRoleWithWebIdentity failed (${response.status}): ${errorXml}`);
  }
  const xml = await response.text();
  const get = (tag: string) => xml.match(new RegExp(`<${tag}>([^<]+)</${tag}>`))?.[1] || '';
  const creds: Credentials = {
    accessKeyId: get('AccessKeyId'), secretAccessKey: get('SecretAccessKey'),
    sessionToken: get('SessionToken'), expiration: new Date(get('Expiration')).getTime(),
  };
  if (!creds.accessKeyId) throw new Error('STS returned no credentials');
  credentialCache.set(roleArn, creds);
  return creds;
}

async function getCredentialsForRole(roleArn: string): Promise<Credentials> {
  const cached = credentialCache.get(roleArn);
  if (cached && cached.expiration > Date.now() + 300000) return cached;
  if (!credentialPromises.has(roleArn)) {
    credentialPromises.set(roleArn, fetchCredentialsForRole(roleArn).finally(() => { credentialPromises.delete(roleArn); }));
  }
  return credentialPromises.get(roleArn)!;
}

async function signRequest(method: string, url: URL, body?: string, roleArn?: string): Promise<Record<string, string>> {
  const creds = await getCredentialsForRole(roleArn || APP_ROLE_ARN);
  const now = new Date();
  const dateStamp = now.toISOString().replace(/[-:]/g, '').substring(0, 8);
  const amzDate = now.toISOString().replace(/[-:]/g, '').substring(0, 15) + 'Z';
  const payloadHash = await sha256(body || '');

  const canonicalQueryString = [...url.searchParams.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

  const extraHeaders: Record<string, string> = {};
  if (body) extraHeaders['content-type'] = 'application/json';

  const allSignedHeaderNames = ['host', ...Object.keys(extraHeaders), 'x-amz-content-sha256', 'x-amz-date', 'x-amz-security-token'].sort();
  const signedHeaders = allSignedHeaderNames.join(';');

  const headerValues: Record<string, string> = {
    host: url.hostname,
    ...extraHeaders,
    'x-amz-content-sha256': payloadHash,
    'x-amz-date': amzDate,
    'x-amz-security-token': creds.sessionToken,
  };
  const canonicalHeaders = allSignedHeaderNames.map(h => `${h}:${headerValues[h]}\n`).join('');

  const canonicalUri = url.pathname.split('/').map(s => encodeURIComponent(s)).join('/');
  const canonicalRequest = `${method}\n${canonicalUri}\n${canonicalQueryString}\n${canonicalHeaders}\n${signedHeaders}\n${payloadHash}`;
  const region = url.hostname.match(/\.execute-api\.([^.]+)\./)?.[ 1] || AWS_REGION;
  const scope = `${dateStamp}/${region}/execute-api/aws4_request`;
  const stringToSign = `AWS4-HMAC-SHA256\n${amzDate}\n${scope}\n${await sha256(canonicalRequest)}`;

  const kDate = await hmac(encoder.encode('AWS4' + creds.secretAccessKey), dateStamp);
  const kRegion = await hmac(kDate, region);
  const kService = await hmac(kRegion, 'execute-api');
  const kSigning = await hmac(kService, 'aws4_request');
  const sigKey = await crypto.subtle.importKey('raw', kSigning, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = Array.from(new Uint8Array(await crypto.subtle.sign('HMAC', sigKey, encoder.encode(stringToSign)))).map(b => b.toString(16).padStart(2, '0')).join('');

  const { host: _h, ...sendHeaders } = headerValues;
  return {
    ...sendHeaders,
    'Authorization': `AWS4-HMAC-SHA256 Credential=${creds.accessKeyId}/${scope}, SignedHeaders=${signedHeaders}, Signature=${sig}`,
  };
}

// Backend mode selection:
//  - OFFLINE: local dev with the SQLite proxy (VITE_STORAGE_INVENTORY_LOCAL=true).
//    Requests go to the local proxy over relative paths, unsigned.
//  - SIGNED:  everything else -> SigV4-signed calls to the configured API.
//      * local dev pointed at gamma: base URL + role come from .env.development
//        (VITE_API_BASE_URL, VITE_ROLE_ARN).
//      * deployed (gamma/prod): base URL comes from the per-stage settings.json;
//        role from APP_ROLE_ARN. This is what makes deployed gamma hit gamma and
//        deployed prod hit prod automatically.
const OFFLINE = import.meta.env.DEV && import.meta.env.VITE_STORAGE_INVENTORY_LOCAL === 'true';

function resolveApiBase(): string {
  return import.meta.env.DEV ? (import.meta.env.VITE_API_BASE_URL || '') : API_BASE_URL;
}
function resolveRoleArn(): string {
  return import.meta.env.DEV && import.meta.env.VITE_ROLE_ARN ? import.meta.env.VITE_ROLE_ARN : APP_ROLE_ARN;
}

// Fetch wrapper. OFFLINE hits the local proxy unsigned; every other mode signs
// with SigV4 (Midway -> STS) against the resolved API base. Used by the Storage
// Inventory client (see storageInventoryClient.ts).
export async function simpleFetch<T>(path: string, options?: RequestInit): Promise<T> {
  if (OFFLINE) {
    console.log(`🌐 API Request (local proxy): ${path}`);
    return retryWithBackoff(async () => {
      const response = await fetch(path, { ...options, headers: { 'Cache-Control': 'no-store', ...options?.headers } });
      if (!response.ok) throw new Error(`API Error: ${response.status} - ${await response.text()}`);
      const ct = response.headers.get('content-type');
      return ct?.includes('application/json') ? response.json() : response.text() as T;
    }, { maxRetries: 3, initialDelay: 1000, maxDelay: 5000,
      shouldRetry: (error) => error instanceof TypeError || error.message.includes('API Error: 5') });
  }

  // Signed mode: dev-against-gamma or deployed (gamma/prod).
  if (!import.meta.env.DEV) await settingsPromise;
  const base = resolveApiBase();
  const fullUrl = path.startsWith('https://') ? path : `${base}${path}`;
  console.log(`🌐 API Request (signed): ${fullUrl}`);

  return retryWithBackoff(async () => {
    const url = new URL(fullUrl);
    const method = options?.method || 'GET';
    const body = options?.body as string | undefined;
    const headers = await signRequest(method, url, body, resolveRoleArn());
    const response = await fetch(url.toString(), { method, headers, body });
    console.log(`📡 API Response: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ API Error: ${response.status}`, errorText);
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    const ct = response.headers.get('content-type');
    return ct?.includes('application/json') ? response.json() : response.text() as T;
  }, {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    shouldRetry: (error) => {
      console.error('🔥 Request error:', error.name, error.message);
      if (error instanceof TypeError) return true;
      if (error.message.includes('API Error: 5')) return true;
      return false;
    },
  });
}
